import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BrandLockup } from "@/shell/CandidateFlow";

const LABEL_STYLE: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "#7C7590",
  marginBottom: 8,
};

const FIELD_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  background: "#FFFFFF",
  border: "1px solid #E0DBD3",
  borderRadius: 8,
  padding: "12px 16px",
  fontSize: 14,
  color: "#17131F",
  outline: "none",
};

const FIELD_STYLE_READONLY: React.CSSProperties = {
  ...FIELD_STYLE,
  background: "#F5F3FA",
  color: "#4B5563",
};

export function RecruiterRegister() {
  const [searchParams] = useSearchParams();
  const regToken = searchParams.get("token") || "";

  const [fullName, setFullName]       = useState("");
  const [email, setEmail]             = useState("");
  const [companyName, setCompanyName] = useState("");
  const [password, setPassword]       = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [prefilled, setPrefilled]     = useState(false);
  const [dpaEnabled, setDpaEnabled]   = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);

  // DPA_ENABLED on the server turns on the required Terms + DPA checkbox.
  useEffect(() => {
    fetch("/api/v1/config", { credentials: "include" })
      .then((r) => r.json())
      .then((d: { dpa_enabled?: boolean }) => setDpaEnabled(d.dpa_enabled === true))
      .catch(() => {});
  }, []);

  // If arriving via an invite/onboarding link (?token=...), look up the
  // invite and prefill + lock name/email/company so only a password is needed.
  useEffect(() => {
    if (!regToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/v1/recruiter/reg-token/${encodeURIComponent(regToken)}`, {
          credentials: "include",
        });
        const json = await res.json() as { ok: boolean; email?: string; contact_name?: string; firm_name?: string };
        if (cancelled || !json.ok) return;
        setFullName(json.contact_name ?? "");
        setEmail(json.email ?? "");
        setCompanyName(json.firm_name ?? "");
        setPrefilled(true);
      } catch {
        // Invite lookup failing just means the form falls back to a blank,
        // editable state — submit-time validation still applies.
      }
    })();
    return () => { cancelled = true; };
  }, [regToken]);

  const canSubmit = !busy && fullName.trim() !== "" && email.trim() !== "" && companyName.trim() !== "" && password !== "" && confirmPassword !== "" && (!dpaEnabled || dpaAccepted);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      // 1. Fetch a fresh CSRF token from the JSON endpoint.
      const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
      if (!csrfRes.ok) throw new Error("Could not obtain security token. Please refresh and try again.");
      const csrfData = (await csrfRes.json()) as { token?: string; csrf_token?: string };
      const csrfToken = csrfData.token ?? csrfData.csrf_token ?? "";

      // 2. POST as form-encoded — Flask reads request.form.
      //    Field names match routes.py auth_register(): contact_name, email, agency_name,
      //    password, password_confirm, reg_token.
      const body = new URLSearchParams();
      body.set("contact_name", fullName.trim());
      body.set("email", email.trim());
      body.set("agency_name", companyName.trim());
      body.set("password", password);
      body.set("password_confirm", confirmPassword);
      if (regToken) body.set("reg_token", regToken);
      if (dpaEnabled && dpaAccepted) body.set("dpa_accept", "1");
      body.set("csrf_token", csrfToken);

      const res = await fetch("/auth/register", {
        method: "POST",
        credentials: "include",
        redirect: "follow",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": csrfToken,
        },
        body: body.toString(),
      });

      const json = await res.json() as { ok: boolean; error?: string; next?: string };
      if (json.ok && json.next) {
        window.location.href = json.next;
        return;
      }
      if (!json.ok) {
        setError(json.error ?? "Registration failed. Please check your details and try again.");
        setBusy(false);
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }

    setBusy(false);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FBFAFE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 16,
          padding: 40,
          boxShadow: "0 1px 4px rgba(45,30,90,0.08)",
        }}
      >
        <div style={{ display: "flex", marginBottom: 28 }}>
          <BrandLockup />
        </div>

        {/* Heading */}
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 26,
            fontWeight: 700,
            color: "#17131F",
            letterSpacing: "-0.4px",
            lineHeight: 1.2,
          }}
        >
          Create your account
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: 14,
            color: "#7C7590",
          }}
        >
          {prefilled ? "Set a password to finish setting up your portal." : "Start your free trial."}
        </p>

        {/* Error alert */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: "12px 14px",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              fontSize: 13.5,
              color: "#B91C1C",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Full name */}
          <div>
            <label htmlFor="reg-full-name" style={LABEL_STYLE}>
              Full name
            </label>
            <input
              id="reg-full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus={!prefilled}
              readOnly={prefilled}
              autoComplete="name"
              placeholder="Jane Smith"
              style={prefilled ? FIELD_STYLE_READONLY : FIELD_STYLE}
              onFocus={(e) => { if (!prefilled) e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { if (!prefilled) e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="reg-email" style={LABEL_STYLE}>
              Email
            </label>
            <input
              id="reg-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              readOnly={prefilled}
              autoComplete="email"
              placeholder="you@firm.com"
              style={prefilled ? FIELD_STYLE_READONLY : FIELD_STYLE}
              onFocus={(e) => { if (!prefilled) e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { if (!prefilled) e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Company name */}
          <div>
            <label htmlFor="reg-company" style={LABEL_STYLE}>
              Company name
            </label>
            <input
              id="reg-company"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              readOnly={prefilled}
              autoComplete="organization"
              placeholder="Acme Executive Search"
              style={prefilled ? FIELD_STYLE_READONLY : FIELD_STYLE}
              onFocus={(e) => { if (!prefilled) e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { if (!prefilled) e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="reg-password" style={LABEL_STYLE}>
              Password
            </label>
            <input
              id="reg-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus={prefilled}
              autoComplete="new-password"
              placeholder="••••••••"
              style={FIELD_STYLE}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Confirm password */}
          <div>
            <label htmlFor="reg-confirm-password" style={LABEL_STYLE}>
              Confirm password
            </label>
            <input
              id="reg-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="••••••••"
              style={FIELD_STYLE}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {dpaEnabled && (
            <label htmlFor="reg-dpa" style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: "#4B5563", lineHeight: 1.5 }}>
              <input
                id="reg-dpa"
                type="checkbox"
                checked={dpaAccepted}
                onChange={(e) => setDpaAccepted(e.target.checked)}
                required
                style={{ marginTop: 3 }}
              />
              <span>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener" style={{ color: "#7A6BE0" }}>Terms of Service</a>
                {" "}and the{" "}
                <a href="/dpa" target="_blank" rel="noopener" style={{ color: "#7A6BE0" }}>Data Processing Agreement</a>
              </span>
            </label>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: "100%",
              height: 48,
              background: canSubmit ? "#17131F" : "#4B5563",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 9999,
              fontSize: 15,
              fontWeight: 600,
              cursor: canSubmit ? "pointer" : "default",
              opacity: canSubmit ? 1 : 0.6,
              transition: "opacity 0.12s, background 0.12s",
              letterSpacing: "0.01em",
            }}
          >
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>

        {/* Sign in link */}
        <p
          style={{
            marginTop: 24,
            textAlign: "center",
            fontSize: 13.5,
            color: "#7C7590",
          }}
        >
          Already have an account?{" "}
          <a
            href="/auth/login"
            style={{
              color: "#7A6BE0",
              textDecoration: "none",
              fontWeight: 500,
            }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
